import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, getMovieById } from '@/mocks/movies';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movieId = Number(id);
  const movie = getMovieById(movieId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [movieId]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center text-foreground-400">
              <i className="ri-error-warning-line text-5xl"></i>
            </div>
            <h2 className="mt-4 text-xl font-bold text-foreground-950">影片未找到</h2>
            <p className="mt-2 text-foreground-500">抱歉，您查找的影片不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 bg-primary-500 text-background-50 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const otherRecommended = movies
    .filter((m) => m.category !== movie.category)
    .slice(0, 6);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Movie',
        name: movie.title,
        description: movie.plot,
        image: movie.image,
        dateCreated: `${movie.year}`,
        director: { '@type': 'Person', name: movie.director },
        actor: movie.actors.map((a) => ({ '@type': 'Person', name: a })),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: movie.rating,
          bestRating: '10',
          worstRating: '0',
          ratingCount: Math.floor(Math.random() * 50000) + 10000,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mahua-cinema.readdy.ai' },
          { '@type': 'ListItem', position: 2, name: movie.categoryName, item: `https://mahua-cinema.readdy.ai/category/${movie.category}` },
          { '@type': 'ListItem', position: 3, name: movie.title, item: `https://mahua-cinema.readdy.ai/movie/${movie.id}` },
        ],
      },
      {
        '@type': 'WebPage',
        name: `麻花影院 - ${movie.title}`,
        url: `https://mahua-cinema.readdy.ai/movie/${movie.id}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <main>
            {/* Hero Banner */}
            <section className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${movie.image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
              </div>

              <div className="absolute inset-0 flex items-center">
                <div className="w-full px-4 md:px-6">
                  <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 md:gap-10">
                    <div className="hidden md:block w-48 lg:w-56 flex-shrink-0">
                      <div className="aspect-[2/3] rounded-lg overflow-hidden">
                        <img src={movie.image} alt={movie.title} className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {movie.isNew && (
                          <span className="bg-accent-500 text-background-950 text-xs font-bold px-2 py-0.5 rounded-md whitespace-nowrap">NEW</span>
                        )}
                        {movie.isHot && (
                          <span className="bg-primary-500 text-background-50 text-xs font-bold px-2 py-0.5 rounded-md whitespace-nowrap">热门</span>
                        )}
                        <span className="text-xs text-foreground-500 bg-background-200 px-2 py-0.5 rounded-md whitespace-nowrap">{movie.categoryName}</span>
                      </div>
                      <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-2">
                        {movie.title}
                      </h1>
                      <p className="text-sm text-foreground-600 max-w-lg mb-4">{movie.description}</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500"></i>
                          </div>
                          <span className="text-accent-400 font-bold text-lg">{movie.rating}</span>
                          <span className="text-foreground-400">/ 10</span>
                        </div>
                        <span>|</span>
                        <span>{movie.year}</span>
                        <span>|</span>
                        <span>{movie.duration}</span>
                        <span>|</span>
                        <span>导演：{movie.director}</span>
                      </div>
                      <p className="text-sm text-foreground-500">
                        <strong>主演：</strong>{movie.actors.join(' / ')}
                      </p>
                      <button className="inline-flex items-center gap-2 mt-5 bg-primary-500 text-background-50 px-8 py-3 rounded-lg font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap w-fit">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-play-fill text-lg"></i>
                        </div>
                        立即播放
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Plot Summary */}
            <section className="px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
                      剧情简介
                    </h2>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed whitespace-pre-line">
                      {movie.plot}
                    </p>

                    {/* Movie Info Table */}
                    <div className="mt-8 bg-background-100 rounded-lg p-5">
                      <h3 className="font-heading font-bold text-foreground-950 mb-4">影片信息</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-foreground-400">片名：</span>
                          <span className="text-foreground-700">{movie.title}</span>
                        </div>
                        <div>
                          <span className="text-foreground-400">导演：</span>
                          <span className="text-foreground-700">{movie.director}</span>
                        </div>
                        <div>
                          <span className="text-foreground-400">主演：</span>
                          <span className="text-foreground-700">{movie.actors.join(' / ')}</span>
                        </div>
                        <div>
                          <span className="text-foreground-400">类型：</span>
                          <Link to={`/category/${movie.category}`} className="text-primary-400 hover:text-primary-300 cursor-pointer">
                            {movie.categoryName}
                          </Link>
                        </div>
                        <div>
                          <span className="text-foreground-400">年份：</span>
                          <span className="text-foreground-700">{movie.year}</span>
                        </div>
                        <div>
                          <span className="text-foreground-400">片长：</span>
                          <span className="text-foreground-700">{movie.duration}</span>
                        </div>
                        <div>
                          <span className="text-foreground-400">评分：</span>
                          <span className="text-accent-400 font-bold">{movie.rating}</span>
                        </div>
                        <div>
                          <span className="text-foreground-400">更新：</span>
                          <span className="text-foreground-700">{movie.updateDate}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sidebar */}
                  <aside>
                    <h3 className="font-heading font-bold text-foreground-950 mb-4">同类推荐</h3>
                    <div className="space-y-3">
                      {relatedMovies.length > 0 ? (
                        relatedMovies.map((m) => (
                          <Link
                            key={m.id}
                            to={`/movie/${m.id}`}
                            className="flex gap-3 bg-background-100 rounded-lg p-3 hover:bg-background-200 transition-colors cursor-pointer"
                          >
                            <div className="w-16 flex-shrink-0 aspect-[2/3] rounded overflow-hidden">
                              <img src={m.image} alt={m.title} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-bold text-foreground-950 truncate">{m.title}</h4>
                              <p className="text-xs text-foreground-500 mt-1">{m.categoryName} · {m.year}</p>
                              <div className="flex items-center gap-1 mt-1">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-star-fill text-[10px] text-accent-500"></i>
                                </div>
                                <span className="text-xs text-accent-400 font-bold">{m.rating}</span>
                              </div>
                            </div>
                          </Link>
                        ))
                      ) : (
                        <p className="text-sm text-foreground-500">暂无同类推荐</p>
                      )}
                    </div>
                  </aside>
                </div>
              </div>
            </section>

            {/* Recommended */}
            <section className="px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">
                  猜你喜欢
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {otherRecommended.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </section>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}